// src/features/mcbimItems/hooks/usePrefetchMcBimItemsPerIfcClass.ts
import { useMemo, useEffect } from "react";
import { openapi, PostMcbimItemsApiArg } from "../../openapi";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { IfcClassesEnum } from "@multiconsult-group/multiplacer-core";
import { selectProjectNo } from "../globalSelectors";

// Norsk (Bokmål): Hjelpefunksjon for stabile, sorterte "aktive" nøkler
const getActiveKeysSorted = (map: Partial<Record<string, boolean>>): string[] => {
  const active = Object.entries(map ?? {})
    .filter(([, isOn]) => isOn === true)
    .map(([k]) => k);

  active.sort((a, b) => String(a).localeCompare(String(b)));
  return active;
};

export const usePrefetchMcBimItemsPerIfcClass = (): void => {
  const dispatch = useAppDispatch();

  const projectNo = useAppSelector(selectProjectNo);
  const floorAndJob = useAppSelector((s) => s.design.floorAndJob);

  // Norsk (Bokmål): Ny mode i redux: "INSTANCE_CLASS" | "DISCIPLINE"
  const mcbimItemsFilterMode = useAppSelector((s) => s.design.mcbimItemsFilterMode);

  // Eksisterende
  const ifcClassesToDisplay = useAppSelector((s) => s.design.ifcClassesToDisplay);

  // Ny
  const disciplinesToDisplay = useAppSelector((s) => s.design.disciplinesToDisplay);

  const buildingStoryName = floorAndJob?.floorName ?? null;

  const activeClassesSorted = useMemo(() => {
    return getActiveKeysSorted(ifcClassesToDisplay).map((k) => k as IfcClassesEnum);
  }, [ifcClassesToDisplay]);

  const activeDisciplinesSorted = useMemo(() => {
    return getActiveKeysSorted(disciplinesToDisplay);
  }, [disciplinesToDisplay]);

  // Norsk (Bokmål): Backend krever i dag instanceClasses. I DISCIPLINE-modus:
  // - hvis vi har valgte klasser, sender vi dem
  // - ellers sender vi en "safe default" for å holde requesten gyldig
  const instanceClassesForDisciplineMode = useMemo(() => {
    if (activeClassesSorted.length > 0) return activeClassesSorted;
    return [IfcClassesEnum.IFCAIRTERMINAL];
  }, [activeClassesSorted]);

  useEffect(() => {
    if (!projectNo || !buildingStoryName) return;

    // Norsk (Bokmål): Prefetch per valgt IFC-klasse (samme som før)
    if (mcbimItemsFilterMode === "INSTANCE_CLASS") {
      if (activeClassesSorted.length === 0) return;

      const subs = activeClassesSorted.map((instanceClass) => {
        const args: PostMcbimItemsApiArg = {
          body: {
            projectNo,
            buildingStoryName,
            instanceClasses: [instanceClass], // en klasse per request
          },
        };

        return dispatch(
          openapi.endpoints.postMcbimItems.initiate(args, {
            subscribe: true,
            forceRefetch: false,
          })
        );
      });

      return () => {
        subs.forEach((p) => p.unsubscribe());
      };
    }

    // Norsk (Bokmål): Prefetch per valgt disiplin
    if (mcbimItemsFilterMode === "DISCIPLINE") {
      if (activeDisciplinesSorted.length === 0) return;

      const subs = activeDisciplinesSorted.map((discipline) => {
        const args: PostMcbimItemsApiArg = {
          body: {
            projectNo,
            buildingStoryName,
            disciplines: [discipline], // ✅ filtrer per disiplin
          },
        };

        return dispatch(
          openapi.endpoints.postMcbimItems.initiate(args, {
            subscribe: true,
            forceRefetch: false,
          })
        );
      });

      return () => {
        subs.forEach((p) => p.unsubscribe());
      };
    }

    return;
  }, [
    dispatch,
    projectNo,
    buildingStoryName,
    mcbimItemsFilterMode,
    activeClassesSorted,
    activeDisciplinesSorted,
  ]);
};
